import React, { useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../components/LanguageContext";

const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  card: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function PaymentFailed() {
  const { t, language } = useLanguage();
  const q = useQuery();

  // სურვილისამებრ: მიზეზი URL-დან
  const reason = q.get("reason") || "";

  useEffect(() => {
    document.title = t("titles.paymentfailed") || (language === "ka" ? "გადახდა ვერ შესრულდა" : "Payment failed");
  }, [language, t]);

  // სურვილისამებრ: ავტორიდაირექტი
  useEffect(() => {
    const tt = setTimeout(() => {
      window.location.href = "/"; // ან "/cart"
    }, 8000);
    return () => clearTimeout(tt);
  }, []);

  const T = (ka, en) => (language === "ka" ? ka : en);

  return (
    <Wrap>
      <Card>
        <IconWrap aria-hidden data-tone="danger">
          <Burst data-tone="danger" />
          <X>❌</X>
        </IconWrap>

        <Title>
          {T(
            "სამწუხაროდ, გადახდა ვერ შესრულდა",
            "Unfortunately, your payment failed"
          )}
        </Title>

        <P>
          {T(
            "გთხოვთ სცადოთ ხელახლა ან გამოიყენოთ სხვა ბარათი.",
            "Please try again or use a different card."
          )}
        </P>

        {reason && (
          <Reason>
            <strong>{T("მიზეზი:", "Reason:")}</strong> {reason}
          </Reason>
        )}

        <Btns>
          <BtnGhost onClick={() => (window.location.href = "/")}>
            {T("მთავარზე დაბრუნება", "Go home")}
          </BtnGhost>
        </Btns>

        <Hint>
          {T(
            "თუ თანხა ჩამოგეჭრათ და შეკვეთა არ შექმნილა, მოგვწერეთ მხარდაჭერას.",
            "If you were charged but no order was created, please contact support."
          )}
        </Hint>
      </Card>
    </Wrap>
  );
}

/* ======= Styles ======= */

const Wrap = styled.div`
  width: 100%;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding-inline: clamp(12px, 4vw, 24px);
  padding-block: clamp(20px, 5vh, 36px);
  overflow-x: hidden;

  background:
    radial-gradient(1200px 500px at 80% -20%, rgba(161, 90, 90, 0.06), transparent 60%),
    radial-gradient(900px 400px at -10% 110%, rgba(17, 24, 39, 0.05), transparent 55%),
    ${COLORS.subtle};
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  margin: 0 clamp(14px, 4vw, 24px);
  box-sizing: border-box;

  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 18px;
  box-shadow: ${COLORS.shadow};

  padding: clamp(20px, 4vw, 28px) clamp(16px, 4vw, 24px);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    border-top-left-radius: 18px;
    border-top-right-radius: 18px;
    background: linear-gradient(90deg, ${COLORS.brand}, ${COLORS.brandDark});
  }
`;

const pop = keyframes`
  0%   { transform: scale(.85); opacity: .6; }
  60%  { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); }
`;

const pulse = keyframes`
  0%   { transform: scale(1);   opacity: .45; }
  70%  { transform: scale(1.25); opacity: 0; }
  100% { transform: scale(1.25); opacity: 0; }
`;

const IconWrap = styled.div`
  width: clamp(64px, 10vw, 76px);
  height: clamp(64px, 10vw, 76px);
  margin: 4px auto 14px;
  border-radius: 999px;
  border: 1px solid ${COLORS.line};
  display: grid;
  place-items: center;
  position: relative;
  animation: ${pop} .38s ease-out both;

  &[data-tone="danger"]{
    background: rgba(239, 68, 68, .12);
  }
`;

const Burst = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 999px;
  animation: ${pulse} 1.6s ease-out 0.12s both;

  &[data-tone="danger"]{
    background: rgba(239, 68, 68, .16);
  }
`;

const X = styled.span`
  font-size: clamp(26px, 6vw, 32px);
  line-height: 1;
`;

const Title = styled.h1`
  margin: 6px 0 6px;
  font-size: clamp(18px, 4.6vw, 20px);
  font-weight: 900;
  color: ${COLORS.text};
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const P = styled.p`
  margin: 6px 0 0;
  color: ${COLORS.dim};
  font-size: clamp(13px, 3.8vw, 14px);
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const Reason = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px dashed ${COLORS.line};
  border-radius: 12px;
  background: #fff;
  color: ${COLORS.text};
  font-size: 13px;
  text-align: left;
  strong{ font-weight: 900; }
`;

const Btns = styled.div`
  margin-top: 14px;
  display: grid;
  gap: 10px;
`;

const BtnPrimary = styled.button`
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,.10);
  background: #111827;
  color: #fff;
  font-weight: 900;
  cursor: pointer;
  transition: transform .12s ease;
  &:active{ transform: scale(.99); }
`;

const BtnGhost = styled.button`
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,.10);
  background: #fff;
  color: #111827;
  font-weight: 900;
  cursor: pointer;
  transition: transform .12s ease;
  &:active{ transform: scale(.99); }
`;

const Hint = styled.div`
  margin-top: 12px;
  font-size: 12.5px;
  color: ${COLORS.dim};
  font-weight: 700;
`;
